import { createContext } from 'preact'
import { useState, useEffect, useContext } from 'preact/hooks';
import { getBehhhInfo, findConfigs, findAnimals, findLocations, findEvents, findMarkers, findCategories, refreshToken, addElement, updateElement, findEventsType } from '../api/api';
import useTable from './TableContext';
import { useToaster } from './ToasterContext';

export const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [animals, setAnimals] = useState(null);
  const [locations, setLocations] = useState(null);
  const [events, setEvents] = useState(null);
  const [markers, setMarkers] = useState(null);
  const [categories, setCategories] = useState(null);
  const [selectedAnimals, setSelectedAnimals] = useState([]);
  const [config, setConfig] = useState(null);
  const [eventsType, setEventsType] = useState(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const { tableData, setTableData } = useTable();
  const addToast = useToaster();

  const fillData = async (tableData, setTableData, setAnimals, setLocations, setMarkers, setEvents, setCategories, setConfig) => {
    const errors = [];
    const tableDataCopy = JSON.parse(JSON.stringify(tableData));
    const displayArchive = localStorage.getItem('displayArchive') && localStorage.getItem('displayArchive') == "true";

    await findAnimals(displayArchive)
      .then((animals) => {
        setAnimals(animals);
        tableDataCopy["animals"] = animals;
      })
      .catch(err => {
        errors.push('Impossible de récupérer la liste des animaux : ' + err);
      });

    await findLocations()
      .then((locations) => {
        setLocations(locations);
        tableDataCopy["locations"] = locations;
      })
      .catch(err => {
        errors.push('Impossible de récupérer la liste des terrains : ' + err);
      });

    await findEvents(displayArchive)
      .then((events) => {
        setEvents(events);
        tableDataCopy["events"] = events;
      })
      .catch(err => {
        errors.push('Impossible de récupérer la liste des évènements : ' + err);
      });

    await findMarkers()
      .then((markers) => {
        setMarkers(markers)
      })
      .catch(err => {
        errors.push('Impossible de récupérer la liste des marqueurs : ' + err);
      });

    await findCategories()
      .then((categories) => {
        setCategories(categories)
      })
      .catch(err => {
        errors.push('Impossible de récupérer la liste des catégories : ' + err);
      });

    await findConfigs()
      .then(res => {
        const config = {}
        res.forEach((cfg) => {
          config[cfg.name] = cfg.value;
        });
        setConfig(config);
      })
      .catch(err => {
        errors.push('Impossible de récupérer les notes : ' + err.message + ' (' + err.cause + ')');
      })

    await findEventsType()
      .then(eventsType => {
        setEventsType(eventsType);
      })
      .catch(err => {
        errors.push('Impossible de récupérer les evts type : ' + err.message + ' (' + err.cause + ')');
      })

    setTableData(tableDataCopy);
    if (errors.length > 0) throw new Error(errors);
  }

  const checkToken = async () => {
    let doRefresh = false;
    let res = false;
    await getBehhhInfo().then(resp => {
      if (resp.version) res = true;
      else if (resp.code && resp.code == '401') {
        // Refresh the token in case of an outdated token
        doRefresh = true;
      }
    })
    if (doRefresh) {
      await refreshToken()
        .catch(err => {
          addToast('danger', 'Impossible de récupérer le token de connexion : ' + err.message);
          localStorage.setItem('bearerToken', '');
          localStorage.setItem('refreshToken', '');
          localStorage.setItem('username', '');
          localStorage.setItem('connected', '');
          res = false;
        })
        .then((data) => {
          if (typeof data != 'undefined' && data.token) {
            localStorage.setItem('bearerToken', data.token);
            localStorage.setItem('refreshToken', data.refresh_token);
            res = true;
          }
          else {
            res = false;
          }
        })
    }
    return new Promise((resolve) => resolve(res));
  }

  const loadData = async (tableData, setTableData, setAnimals, setLocations, setMarkers, setEvents, setCategories, setConfig) => {
    await checkToken()
      .then(resp => {
        if (resp) {
          fillData(tableData, setTableData, setAnimals, setLocations, setMarkers, setEvents, setCategories, setConfig)
            .then(() => setIsDataLoaded(true))
            .catch(err => {
              addToast('danger', 'Il y a eu une erreur lors de la récupération des données : ' + err.message);
            })
        }
      })
      .catch(err => {
        addToast('danger', 'Il y a eu une erreur lors de la récupération des données : ' + err.message);
      })
  }

  const manageApiQueue = async () => {
    const apiQueue = localStorage.getItem('api_queue') ? JSON.parse(localStorage.getItem('api_queue')) : [];
    if (apiQueue.length > 0) {
      let success = 0;
      let error = 0;
      addToast('warning', 'Synchronisation des données en cours ...');
      const apiQueueCopy = [];
      await Promise.all(
        apiQueue.map(async (q) => {
          let action;
          if (q.type == 'POST') {
            action = addElement(q.data, q.path);
          }
          else if (q.type == 'PATCH') {
            action = updateElement(q.data, q.path);
          }
          await action
            .then(response => {
              if (response["@context"] && response["@context"] != '/api/contexts/Error') {
                success++;
              } else {
                error++;
                apiQueueCopy.push(q);
              }
            })
            .catch(() => {
              apiQueueCopy.push(q);
              error++;
            });
        })
      )
      localStorage.setItem('api_queue', JSON.stringify(apiQueueCopy));
      if (success > 0 && error == 0) {
        addToast('success', success + ' éléments envoyés');
        return true;
      }
      if (error > 0) {
        addToast('danger', error + ' éléments en erreur');
        return false;
      }
    }
    else addToast('success', 'Aucun élément à synchroniser');
    return true;
  }

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener('offline', function () {
        alert('Attention, vous avez perdu la connection à Internet.');
      });
      window.addEventListener('online', function () {
        alert('Connection à Internet retrouvée, tentative de transmission des données.');
        // Look for api calls in localstorage queue and run them
        if (localStorage.getItem('api_queue') != "") {
          manageApiQueue().then(() => {
            loadData(tableData, setTableData, setAnimals, setLocations, setMarkers, setEvents, setCategories, setConfig);
          });
        }
      });
    }
    loadData(tableData, setTableData, setAnimals, setLocations, setMarkers, setEvents, setCategories, setConfig);
  }, []);

  return (
    <AppContext.Provider value={{ isDataLoaded, animals, setAnimals, selectedAnimals, setSelectedAnimals, locations, setLocations, markers, setMarkers, events, setEvents, categories, setCategories, loadData, manageApiQueue, checkToken, config, setConfig, eventsType, setEventsType }}>
      {children}
    </AppContext.Provider>
  );
};

const useApp = () => {
  const context = useContext(AppContext);

  if (context === undefined) {
    throw new Error("useApp must be used within a AppProvider");
  }
  return context;
};


export default useApp;
